import React from 'react';
import PropTypes from 'prop-types';

import Banner from 'shared/banner';
import Form from './form';
import FormModal from 'components/form_modal';
import LoadingSpinner from 'components/loading_spinner';
import ManagedInput from 'components/form/managed_input';
import typeOptions from 'bundles/backoffice/contacts/type_options';
import { get } from 'helpers/api';
import { saveContact } from 'bundles/backoffice/contacts/api';

import './modal_styles.scss';

const newContact = {
  additionalEmails: [],
  addresses: [],
  businessRecords: [],
  contactType: typeOptions[0].value,
  phoneNumbers: [],
};

class ContactFormModal extends React.Component {
  static propTypes = {
    allowSavingPrivateContacts: PropTypes.bool,
    allowTagging: PropTypes.bool,
    apiHelpers: PropTypes.object,
    backdrop: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    contact: PropTypes.shape({
      path: PropTypes.string,
      private: PropTypes.bool,
    }),
    contactType: PropTypes.string,
    // TODO: Just nodes for now, but should be nodes keyed by section for more organizational control
    extraFormSections: PropTypes.node,
    forceSaveAsRoot: PropTypes.bool,
    forceSaveAsSnapshot: PropTypes.bool,
    forRecord: PropTypes.shape({
      id: PropTypes.number.isRequired,
      type: PropTypes.string.isRequired,
    }),
    multipleBusinesses: PropTypes.bool,
    newContactVisibility: PropTypes.string,
    params: PropTypes.object,
    remote: PropTypes.bool,
    requestPermissions: PropTypes.bool,
    saveButtonClassName: PropTypes.string,
    saveButtonLabel: PropTypes.string,
    show: PropTypes.bool,
    showQuickbooks: PropTypes.bool,
    validateExtraSections: PropTypes.func,
    voucherId: PropTypes.number, // DEPRECATED: Use forRecord instead
    warn: PropTypes.bool,
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired
  };

  state = {
    contact: { ...newContact },
    warningAcknowledged: true,
  };

  get isValid() {
    const { id, contactType, name, firstName, lastName, email } = this.state.contact;
    if (contactType === 'business') {
      return !!name;
    } else {
      // for new contacts, require a first and last, for editing existing, first and last OR email
      return (firstName && lastName && !id) || (id && (firstName && lastName || email));
    }
  }

  reset = async () => {
    let contact = this.props.contact;
    if (this.props.remote) {
      contact = await this.loadContact();
      if (!contact) {
        return;
      }
    }

    this.setState({ saveAsRoot: !this.props.forceSaveAsSnapshot, warningAcknowledged: !this.props.warn });
    this.props.contact ? this.resetFormFromContact(contact) : this.clearForm();
  };

  loadContact = async () => {
    try {
      this.setState({ loading: true });
      return await get(`${this.props.contact.path}.json`);
    } catch {
      this.props.onCancel();
    } finally {
      this.setState({ loading: false });
    }
  };

  clearForm = () => {
    const contact = { ...newContact };

    if (this.props.contactType) {
      contact.contactType = this.props.contactType;
    }

    if (this.props.newContactVisibility) {
      contact.visibility = this.props.newContactVisibility;
    }

    if (this.props.requestPermissions) {
      contact.visibility = contact.visibility || 'public';
    }

    this.setState({ contact });
  };

  resetFormFromContact = (initialContact) => {
    const contact = { ...newContact, ...initialContact };
    contact.additionalEmails = contact.additionalEmails.map(email => ({ ...email }));
    contact.phoneNumbers = contact.phoneNumbers.map((phoneNumber, index) => ({ ...phoneNumber, index }));
    contact.addresses = contact.addresses.map((address, index) => ({ ...address, index }));
    this.setState({ contact });
  };

  handleChange = (changes) => {
    this.setState(({ contact }) => ({
      contact: {
        ...contact,
        ...changes
      }
    }));
  };

  cancel = () => {
    this.props.onCancel();
  };

  handleSubmit = () => {
    if (this.props.validateExtraSections) {
      const errors = this.props.validateExtraSections();
      if (errors) {
        return new Promise((_, reject) => {
          reject(errors);
        });
      }
    }

    const forRecord = this.props.voucherId
      ? { id: this.props.voucherId, type: 'Backoffice::Voucher' }
      : this.props.forRecord;

    return saveContact(this.state.contact, forRecord, {
      allowSavingPrivateContacts: this.props.allowSavingPrivateContacts,
      apiHelpers: this.props.apiHelpers,
      params: this.props.params,
      saveAsRoot: this.state.saveAsRoot,
    });
  };

  render() {
    let content;
    if (this.state.loading) {
      content = <LoadingSpinner/>;
    } else {
      content = <>
        {this.props.warn && !this.state.warningAcknowledged &&
          <Banner
            callToAction={{
              buttonText: 'Edit',
              onClick: () => this.setState({ warningAcknowledged: true }),
            }}
            className="mb-4"
            content="This is a shared contact, edits made here will apply everywhere. Are you sure you want to edit?"
            variant="warning"
          />
        }

        <Form
          allowSavingPrivateContacts={this.props.allowSavingPrivateContacts}
          allowTagging={this.props.allowTagging}
          contact={this.state.contact}
          contactType={this.props.contactType}
          extraFormSections={this.props.extraFormSections}
          multipleBusinesses={this.props.multipleBusinesses}
          requestPermissions={this.props.requestPermissions}
          showQuickbooks={this.props.showQuickbooks}
          onChange={this.handleChange}
        />
      </>;
    }

    return (
      <FormModal
        backdrop={this.props.backdrop}
        className="backoffice-contact-form-modal"
        disabled={this.state.loading || !this.isValid || !this.state.warningAcknowledged}
        footerContent={this.renderSaveAsRoot()}
        header={this.props.contact ? 'Edit Contact' : 'New Contact'}
        saveButtonClassName={this.props.saveButtonClassName}
        saveButtonLabel={this.props.saveButtonLabel}
        show={this.props.show}
        size="lg"
        onCancel={this.props.onCancel}
        onShow={this.reset}
        onSubmit={this.handleSubmit}
        onSuccess={this.props.onSave}
      >
        {content}
      </FormModal>
    );
  }

  renderSaveAsRoot() {
    if (
      this.props.contact
      && !this.props.contact.private
      && !this.props.forceSaveAsRoot
      && !this.props.forceSaveAsSnapshot
    ) {
      return (
        <ManagedInput
          name="saveAsRoot"
          type="checkbox"
          value={this.state.saveAsRoot}
          onValueChange={saveAsRoot => this.setState({ saveAsRoot })}
        >
          New records should use this contact info
        </ManagedInput>
      );
    }
  }
}

export default ContactFormModal;
